const MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024;

const ALLOWED_DOCUMENT_TYPES = new Set([
  'application/pdf',
  'application/x-pdf',
  'image/jpeg',
  'image/jpg',
  'image/pjpeg',
  'image/png',
  'image/gif',
  'image/tiff',
  'image/x-tiff',
  'application/msword',
  'application/vnd.ms-word',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'text/csv',
  'application/csv',
  'application/xml',
  'text/xml',
]);

const ALLOWED_DOCUMENT_EXTENSIONS = new Set([
  'pdf',
  'jpg',
  'jpeg',
  'png',
  'gif',
  'tiff',
  'tif',
  'doc',
  'docx',
  'xlsx',
  'csv',
  'xml',
]);

export const DOCUMENT_ATTACHMENT_ACCEPT =
  '.pdf,.jpg,.jpeg,.png,.gif,.tiff,.tif,.doc,.docx,.xlsx,.csv,.xml';

export type AttachmentValidationResult =
  | { valid: true }
  | { valid: false; message: string };

const fileExtension = (filename: string) => {
  const parts = filename.toLowerCase().split('.');
  return parts.length > 1 ? parts[parts.length - 1] : '';
};

export const validateDocumentAttachment = (file: File): AttachmentValidationResult => {
  const ext = fileExtension(file.name);
  const extAllowed = ext ? ALLOWED_DOCUMENT_EXTENSIONS.has(ext) : false;
  const mime = file.type?.toLowerCase() ?? '';
  const typeAllowed =
    mime.length > 0 &&
    mime !== 'application/octet-stream' &&
    ALLOWED_DOCUMENT_TYPES.has(mime);

  if (!typeAllowed && !extAllowed) {
    return {
      valid: false,
      message: `${file.name}: must be PDF, image, DOC, DOCX, XLSX, CSV, or XML.`,
    };
  }

  if (file.size > MAX_ATTACHMENT_BYTES) {
    return {
      valid: false,
      message: `${file.name}: must be 20 MB or smaller.`,
    };
  }

  if (file.size === 0) {
    return {
      valid: false,
      message: `${file.name}: file is empty.`,
    };
  }

  return { valid: true };
};

export const formatAttachmentByteSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
